import { FC, useState, useEffect } from "react";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useFormik } from "formik";
import { ApisService } from "../../services/api";
import { Formatter } from "../../utils/formatters";
import { Input as MaterialInput } from "@material-tailwind/react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { NoSearchEngineTemplate } from "templates/NoSearchEngineTemplate";
import {
  FaRegEye as EyeOpen,
  FaRegEyeSlash as EyeClosed,
} from "react-icons/fa";
import { FaArrowLeft as LeftIcon } from "react-icons/fa6";

import { PublicRoute } from "routers/PublicRoute";
import { PageSignUpProps, FormValues } from "./type";
import { validationSchema } from "./schema";
import { validateCompanyToken } from "services/validate-company-token";
import { CreateAccountParams } from "types/userCreate";
import { AlertError } from "shared/AlertError";

const PageSignUp: FC<PageSignUpProps> = ({ className = "" }) => {
  const [showAlert, setShowAlert] = useState({
    status: false,
    code: 0,
    message: "",
  });

  const [companyData, setCompanyData] = useState<{
    name: string;
    logo_url: string;
  } | null>(null);

  const location = useLocation();
  const companyToken = new URLSearchParams(location.search).get(
    "company_token"
  );
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({
    password: false,
    newPassword: false,
    code: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const validateToken = async () => {
      if (!companyToken) {
        return;
      }

      const response = await validateCompanyToken(companyToken);
      if (response.type === "error") {
        setShowAlert({
          status: true,
          code: response.status ?? 500,
          message: response.message ?? "",
        });
      }

      if (response.type === "success") {
        setCompanyData(response.company ?? null);
      }
    };

    validateToken();
  }, [companyToken]);

  const handleError = <T extends FormValues>(
    fieldName: keyof T,
    formik: any
  ) => {
    if (formik.touched[fieldName] && Boolean(formik.errors[fieldName])) {
      return `mt-1 border border-red-500 focus:ring-2 focus:outline-none focus:ring-red-500 focus:border-red-500`;
    }
    return `mt-1 border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`;
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      fullName: "",
      email: "",
      phoneNumber: "",
      dateOfBirth: "",
      cpf: "",
      password: "",
      confirmPassword: "",
      company_token: companyToken ?? undefined,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const phoneNumber = values.phoneNumber.replace(/\D/g, "");
      const dateOfBirth = Formatter.convertDateBrasilForEua(values.dateOfBirth);

      const params: CreateAccountParams = {
        name: values.fullName,
        email: values.email,
        phone: phoneNumber,
        birth_date: dateOfBirth,
        cpf: values.cpf.replace(/\D/g, ""),
        password: values.password,
        company_token: values.company_token,
      };

      const res = ApisService.createAccount(params);
      toast.promise(
        res,
        {
          loading: "Criando conta...",
          success: () => {
            setTimeout(() => {
              navigate("/");
            }, 3500);
            return "Conta criada com Sucesso!\nPor favor, verifique seu\ne-mail para confirmar.";
          },
          error: (error) => {
            const {
              response: {
                data: { detail },
              },
            } = error;
            return detail;
          },
        },
        {
          style: {
            padding: "18px",
            fontSize: "16px",
          },
          success: {
            duration: 12000,
          },
        }
      );
    },
  });

  const handleClickShowPassword = (field: string) => {
    setShowPassword((prevShowPassword) => ({
      ...prevShowPassword,
      [field]: !prevShowPassword[field],
    }));
  };

  const isPasswordValid = (password: string) => {
    const minLength = password.length >= 8;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);

    return (
      minLength && hasSpecialChar && hasUpperCase && hasLowerCase && hasNumber
    );
  };

  const toLoginPage = () => {
    if (companyToken) {
      navigate(`/?company_token=${companyToken}`);
      return 0;
    }
    navigate("/login");
  };

  return (
    <PublicRoute>
      <NoSearchEngineTemplate>
        <button
          type="button"
          className="flex flex-row items-center ml-5 mt-5 cursor-pointer"
          onClick={() => navigate(-1)}
          onTouchStart={() => navigate(-1)}
        >
          <LeftIcon style={{ marginRight: "10px" }} />
          <span className="text-xl">Voltar</span>
        </button>
        <div className={`nc-PageSignUp  ${className}`} data-nc-id="PageSignUp">
          <Toaster position="top-center" />

          <div className="container mb-10 lg:mb-20">
            {companyData?.logo_url ? (
              <div className=" w-max mx-auto mb-10">
                <img
                  src={new URL(companyData.logo_url).toString()}
                  alt="Logo da empresa"
                  className="w-36"
                />
              </div>
            ) : null}
            <h2 className="mb-8 -mt-4 flex items-center focus:ring- text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
              Cadastrar
            </h2>
            <div className="max-w-md mx-auto space-y-6 ">
              {/* FORM */}
              <form
                className="grid grid-cols-1 gap-6"
                action="#"
                method="post"
                onSubmit={formik.handleSubmit}
              >
                <label className="block">
                  <span className="flex items-center text-neutral-800 dark:text-neutral-200">
                    Nome completo <span className="text-[#d32f2f] ml-1">*</span>
                  </span>
                  <Input
                    type="input"
                    className={handleError("fullName", formik)}
                    placeholder="Nome Exemplo"
                    id="fullName"
                    name="fullName"
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.fullName && formik.touched.fullName && (
                    <span className="text-[#d32f2f] mt-2 mr-4 mb-0 ml-3 text-left text-xs">
                      {formik.errors.fullName}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    Email pessoal <span className="text-[#d32f2f] ml-1">*</span>
                  </span>
                  <Input
                    type="email"
                    placeholder="example@example.com"
                    className={handleError("email", formik)}
                    id="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                  />
                  {formik.errors.email && formik.touched.email && (
                    <span className="text-[#d32f2f] mt-2 mr-4 mb-0 ml-3 text-left text-xs">
                      {formik.errors.email}
                    </span>
                  )}
                </label>

                <label className="block">
                  <span className="flex items-center text-neutral-800 dark:text-neutral-200">
                    Telefone <span className="text-[#d32f2f] ml-1">*</span>
                  </span>
                  <Input
                    type="input"
                    className={handleError("phoneNumber", formik)}
                    placeholder="(XX) XXXXX - XXXX"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={Formatter.prettyPhone(formik.values.phoneNumber)}
                    onChange={(e) => {
                      const formattedPhoneNumber = Formatter.prettyPhone(
                        e.target.value
                      );
                      formik.handleChange({
                        target: {
                          name: "phoneNumber",
                          value: formattedPhoneNumber,
                        },
                      });
                    }}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.phoneNumber && formik.touched.phoneNumber && (
                    <span className="text-[#d32f2f] mt-2 mr-4 mb-0 ml-3 text-left text-xs">
                      {formik.errors.phoneNumber}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="flex items-center text-neutral-800 dark:text-neutral-200">
                    Data de nascimento{" "}
                    <span className="text-[#d32f2f] ml-1">*</span>
                  </span>
                  <Input
                    type="input"
                    className={handleError("dateOfBirth", formik)}
                    placeholder="xx/xx/xxxx"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formik.values.dateOfBirth}
                    onChange={(e) => {
                      const formattedPhoneNumber = Formatter.prettyDateString(
                        e.target.value
                      );
                      formik.handleChange({
                        target: {
                          name: "dateOfBirth",
                          value: formattedPhoneNumber,
                        },
                      });
                    }}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.dateOfBirth && formik.touched.dateOfBirth && (
                    <span className="text-[#d32f2f] mt-2 mr-4 mb-0 ml-3 text-left text-xs">
                      {formik.errors.dateOfBirth}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="flex items-center text-neutral-800 dark:text-neutral-200">
                    CPF <span className="text-[#d32f2f] ml-1">*</span>
                  </span>
                  <Input
                    type="input"
                    className={handleError("cpf", formik)}
                    placeholder="XXX.XXX.XXX-XX"
                    id="cpf"
                    name="cpf"
                    value={Formatter.prettyCPF(formik.values.cpf)}
                    onChange={(e) => {
                      const formattedCPF = Formatter.prettyCPF(e.target.value);
                      formik.handleChange({
                        target: {
                          name: "cpf",
                          value: formattedCPF,
                        },
                      });
                    }}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.cpf && formik.touched.cpf && (
                    <span className="text-[#d32f2f] mt-2 mr-4 mb-0 ml-3 text-left text-xs">
                      {formik.errors.cpf}
                    </span>
                  )}
                </label>

                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    Senha <span className="text-[#d32f2f] ml-1">*</span>
                  </span>
                  <MaterialInput
                    type={showPassword["password"] ? "text" : "password"}
                    id="password"
                    placeholder="Senha"
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onDrag={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                    className={`mt-1 mb-1 ${
                      formik.touched.password && formik.errors.password
                        ? "focus:ring-red-500 focus:border-opacity-50 focus:border-red-500 border-red-500"
                        : ""
                    }`}
                    {...formik.getFieldProps("password")}
                    icon={
                      showPassword["password"] ? (
                        <EyeClosed
                          onClick={() => handleClickShowPassword("password")}
                          className="mt-3 cursor-pointer"
                        />
                      ) : (
                        <EyeOpen
                          onClick={() => handleClickShowPassword("password")}
                          className="mt-3 cursor-pointer"
                        />
                      )
                    }
                  />
                  {formik.touched.password && formik.errors.password ? (
                    <div className="text-red-500 text-xs">
                      {formik.errors.password}
                    </div>
                  ) : null}
                </label>

                <div className="ml-4 text-green-500">
                  <span
                    className={`${
                      isPasswordValid(formik.values.password)
                        ? ""
                        : "text-red-500"
                    }`}
                  >
                    Tamanho mínimo de 8 caracteres
                  </span>
                  <br />
                  <span
                    className={`${
                      /[!@#$%^&*(),.?":{}|<>]/.test(formik.values.password)
                        ? ""
                        : "text-red-500"
                    }`}
                  >
                    Deve conter pelo menos 1 caractere especial
                  </span>
                  <br />
                  <span
                    className={`${
                      /[A-Z]/.test(formik.values.password) ? "" : "text-red-500"
                    }`}
                  >
                    Deve conter pelo menos 1 letra maiúscula
                  </span>
                  <br />
                  <span
                    className={`${
                      /[a-z]/.test(formik.values.password) ? "" : "text-red-500"
                    }`}
                  >
                    Deve conter pelo menos 1 letra minúscula
                  </span>
                  <br />
                  <span
                    className={`${
                      /\d/.test(formik.values.password) ? "" : "text-red-500"
                    }`}
                  >
                    Deve conter pelo menos 1 número
                  </span>
                </div>

                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    Confirmar Senha{" "}
                    <span className="text-[#d32f2f] ml-1">*</span>
                  </span>
                  <MaterialInput
                    type={
                      showPassword["confirmNewPassword"] ? "text" : "password"
                    }
                    id="confirmPassword"
                    placeholder="Confirmar Senha"
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onDrag={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                    className={`mt-1 mb-1 ${
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                        ? "focus:ring-red-500 focus:border-opacity-50 focus:border-red-500 border-red-500"
                        : ""
                    }`}
                    {...formik.getFieldProps("confirmPassword")}
                    icon={
                      showPassword["confirmNewPassword"] ? (
                        <EyeClosed
                          onClick={() =>
                            handleClickShowPassword("confirmNewPassword")
                          }
                          className="mt-3 cursor-pointer"
                        />
                      ) : (
                        <EyeOpen
                          onClick={() =>
                            handleClickShowPassword("confirmNewPassword")
                          }
                          className="mt-3 cursor-pointer"
                        />
                      )
                    }
                  />

                  {formik.touched.confirmPassword &&
                  formik.errors.confirmPassword ? (
                    <div className="text-[#d32f2f] mt-2 mr-4 mb-0 ml-3 text-left text-xs">
                      {formik.errors.confirmPassword}
                    </div>
                  ) : null}
                </label>

                <ButtonPrimary
                  className="bg-[--blue] hover:bg-[--blue-dark] disabled:bg-[#b2d0e0] disabled:cursor-not-allowed"
                  type="submit"
                  // disabled={formik.isSubmitting/ || !formik.isValid}
                  disabled={!formik.isValid}
                >
                  Continue
                </ButtonPrimary>

                <div className="flex items-center">
                  <span className="ml-2 block text-xs text-neutral-800 dark:text-neutral-200 ">
                    Ao se cadastrar, você concorda com nossos{" "}
                    <Link
                      to="https://virotrip-public.s3.amazonaws.com/policies/termos-de-uso.pdf"
                      target="_blank"
                      className="text-blue underline"
                    >
                      Termos e Condições
                    </Link>{" "}
                    e{" "}
                    <Link
                      to="https://virotrip-public.s3.amazonaws.com/policies/politicas-privacidade.pdf"
                      target="_blank"
                      className="text-blue underline"
                    >
                      Política de Privacidade
                    </Link>
                  </span>
                </div>
              </form>
              {/* ==== */}
              <span className="block text-center text-neutral-700 dark:text-neutral-300">
                Já possui uma conta? {` `}
                <button onClick={toLoginPage} className="text-blue underline">
                  Entre
                </button>
              </span>
            </div>
          </div>
        </div>

        {showAlert.status ? (
          <AlertError code={showAlert.code} />
        ) : null}
      </NoSearchEngineTemplate>
    </PublicRoute>
  );
};

export default PageSignUp;
